import { alert } from '@repo/ui/Alert'
import { debounce } from '@repo/utils/debounce'
import { formatThousands } from '@repo/utils/numbers'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useUpcomingDatesStore } from '@repo/store/useUpcomingDatesStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import Cookie from 'js-cookie'

const initialState = {
  keyDates: {},
  keyDatesExtended: {},
  keyDatesTableData: [],
  keyDateTypes: {},
  keyDateLocations: [],
  keyDateLocationsById: {},
  keyDatesDrawerIsOpen: false,
  keyDatesDrawerItemId: null,
  keyDatesDrawerMode: 'view',
  triggerKeyDatesTableRefresh: 0
}

export const useKeyDateStore = storeWrapper('useKeyDateStore', (set, get) => ({
  ...initialState,
  toggleKeyDatesDrawer: ({ keyDatesDrawerIsOpen, keyDatesDrawerItemId, keyDatesDrawerMode = 'view' }) => {
    set({
      keyDatesDrawerIsOpen,
      keyDatesDrawerItemId: keyDatesDrawerIsOpen ? keyDatesDrawerItemId : null,
      keyDatesDrawerMode
    })
  },
  getKeyDate: async ({ id, ospreyKeyDate_DI }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchKeyDate-${id}`,
        delay: 300,
        callback: async () => {
          try {
            let fetchedKeyDate

            if (ospreyKeyDate_DI) {
              fetchedKeyDate = ospreyKeyDate_DI({ id })
            } else {
              fetchedKeyDate =
                (await ospreyAPI({
                  service: 'matters',
                  endpoint: 'KeyDate/Get',
                  data: { id },
                  suppressErrorToast: true
                })) || {}
            }

            const keyDates = { ...get().keyDates, [id]: { ...fetchedKeyDate } }
            set({ keyDates })
            resolve(fetchedKeyDate)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  getKeyDateExtended: async ({ id, ospreyKeyDate_DI }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchKeyDateExtended-${id}`,
        delay: 300,
        callback: async () => {
          try {
            let fetchedKeyDateExtended

            if (ospreyKeyDate_DI) {
              fetchedKeyDateExtended = ospreyKeyDate_DI({ id })
            } else {
              fetchedKeyDateExtended =
                (await ospreyAPI({
                  service: 'matters',
                  endpoint: 'KeyDate/GetExtended',
                  data: { keyDateId: id },
                  suppressErrorToast: true
                })) || {}
            }

            const keyDatesExtended = { ...get().keyDatesExtended, [id]: { ...fetchedKeyDateExtended } }
            set({ keyDatesExtended })
            resolve(fetchedKeyDateExtended)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  refreshKeyDatesInComingUp: async () => {
    // refresh upcoming dates feed if needed after changing a key date (i.e. user in on home page or matter workspace)
    const { isHome, path } = useUrlStore.getState()
    const { fetchUpcomingDates } = useUpcomingDatesStore.getState()
    const isMatterWorkspace = path.includes('matters/matter-workspace')

    if (isHome || isMatterWorkspace) {
      fetchUpcomingDates({ isMatterSpecific: isMatterWorkspace })
    }
  },
  refreshKeyDatesInTables: async () => {
    // refresh upcoming dates feed if needed after changing a key date (i.e. user in on home page or matter workspace)
    const { path } = useUrlStore.getState()
    const isAllKeyDatesPage = path.includes('case-management/all-key-dates')
    const isMatterKeyDatesPage = path.includes('case-management/matters/key-dates')

    if (isAllKeyDatesPage || isMatterKeyDatesPage) {
      set({ triggerKeyDatesTableRefresh: get().triggerKeyDatesTableRefresh + 1 })
    }
  },
  createKeyDate: async (data) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createKeyDate`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              service: 'matters',
              endpoint: 'KeyDate/CreateWithAccessToken',
              data,
              method: 'POST'
            })

            get().refreshKeyDatesInComingUp()
            get().refreshKeyDatesInTables()
            resolve(response)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  updateKeyDate: async (data, successToastMessage) => {
    return new Promise((resolve, reject) => {
      const id = data?.keyDateId || ''
      debounce({
        id: `updateKeyDate-${id}`,
        delay: 300,
        callback: async () => {
          try {
            await ospreyAPI({
              service: 'matters',
              endpoint: 'KeyDate/UpdateWithAccessToken',
              data,
              method: 'PUT',
              successToast: successToastMessage
                ? {
                    type: 'success',
                    title: `Key date updated`,
                    text: successToastMessage
                  }
                : null
            })

            get().refreshKeyDatesInComingUp()
            get().refreshKeyDatesInTables()
            resolve()
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  deleteKeyDate: async ({ keyDateId }) => {
    console.log(`deleteKeyDate called with keyDateId:`, keyDateId)
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteKeyDate-${keyDateId}`,
        delay: 300,
        callback: async () => {
          try {
            const { userId } = useAuthenticationStore.getState()
            const msAccessToken = Cookie.get(`${userId}MsToken`) ?? null

            await ospreyAPI({
              service: 'matters',
              endpoint: `KeyDate/DeleteWithAccessToken`,
              method: 'DELETE',
              sendAsUrlParams: true,
              data: {
                id: keyDateId,
                accessToken: msAccessToken
              }
            })

            await get().refreshKeyDatesInComingUp()
            await get().refreshKeyDatesInTables()
            resolve()
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  fetchKeyDateLocations: async () => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchKeyDateLocations`,
        delay: 300,
        callback: async () => {
          const keyDateLocations = await ospreyAPI({
            service: 'matters',
            endpoint: 'KeyDateLocation/GetAll'
          })

          const keyDateLocationsById = {}
          keyDateLocations?.forEach((location) => {
            keyDateLocationsById[location.keyDateLocationId] = location
          })

          set({ keyDateLocations, keyDateLocationsById })
          resolve(keyDateLocations)
        }
      })
    })
  },
  fetchKeyDateTypes: async ({ startAtIndex, pageSize, description }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchKeyDateTypes`,
        delay: 300,
        callback: async () => {
          const { rows, totalRowCount } = await ospreyAPI({
            service: 'matters',
            endpoint: 'KeyDateType/GetAllByDescription',
            data: { startAtIndex, pageSize, description }
          })

          const keyDateTypes = { ...get().keyDateTypes }
          rows?.forEach((type) => {
            keyDateTypes[type.keyDateTypeId] = type
          })

          set({ keyDateTypes })
          resolve({ rows, totalRowCount })
        }
      })
    })
  },
  fetchKeyDateTypeById: async ({ keyDateTypeId }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchKeyDateTypeById-${keyDateTypeId}`,
        delay: 300,
        callback: async () => {
          const keyDateType = await ospreyAPI({
            service: 'matters',
            endpoint: 'KeyDateType/Get',
            data: { id: keyDateTypeId }
          })

          const keyDateTypes = { ...get().keyDateTypes, [keyDateTypeId]: keyDateType }
          set({ keyDateTypes })
          resolve(keyDateType)
        }
      })
    })
  },
  fetchKeyDatesTableData: async ({ keyDatesTableFilters }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchKeyDatesTableData-${keyDatesTableFilters.clientMatterNo}`,
        delay: 300,
        callback: async () => {
          let fetchedKeyDatesTableData

          try {
            const raw = keyDatesTableFilters?.filters ?? {}
            const payload = {
              ...raw,
              // coerce pagination defaults if null/undefined/invalid
              startAtIndex: Number.isInteger(raw.startAtIndex) && raw.startAtIndex >= 0 ? raw.startAtIndex : 0,
              pageSize: Number.isInteger(raw.pageSize) && raw.pageSize > 0 ? raw.pageSize : 25
            }
            // If orderByColumns got built as an empty array, some APIs prefer null
            if (Array.isArray(payload.orderByColumns) && payload.orderByColumns.length === 0) {
              payload.orderByColumns = null
            }

            fetchedKeyDatesTableData =
              (await ospreyAPI({
                method: 'POST',
                service: 'matters',
                endpoint: 'KeyDate/GetAllExtendedBySearchCriteria',
                data: payload
              })) || []
          } catch (err) {
            console.error('Error fetching key dates:', err)
            return
          }

          set({ keyDatesTableData: fetchedKeyDatesTableData })
          resolve(fetchedKeyDatesTableData)
        }
      })
    })
  },
  fetchKeyDatesTableDataAsCSV: async ({ keyDatesTableFilters }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchKeyDatesTableData-${keyDatesTableFilters.clientMatterNo}`,
        delay: 300,
        callback: async () => {
          let fetchedKeyDatesTableDataCSV

          try {
            const raw = keyDatesTableFilters?.filters ?? {}
            const payload = {
              ...raw,
              // coerce pagination defaults if null/undefined/invalid
              startAtIndex: Number.isInteger(raw.startAtIndex) && raw.startAtIndex >= 0 ? raw.startAtIndex : 0,
              pageSize: Number.isInteger(raw.pageSize) && raw.pageSize > 0 ? raw.pageSize : 25
            }
            // If orderByColumns got built as an empty array, some APIs prefer null
            if (Array.isArray(payload.orderByColumns) && payload.orderByColumns.length === 0) {
              payload.orderByColumns = null
            }

            fetchedKeyDatesTableDataCSV =
              (await ospreyAPI({
                method: 'POST',
                service: 'matters',
                endpoint: 'KeyDate/GetAllExtendedBySearchCriteriaAsCsv',
                data: payload,
                suppressErrorToast: true
              })) || []
          } catch (err) {
            if (err.status === 422) {
              alert(
                `You have selected ${formatThousands(keyDatesTableFilters.totalRowCount || 0)} records, but this feature is limited to ${formatThousands(10000)} records. Please refine your search to include fewer results, or contact support@ospreyapproach.com if you require further assistance`
              )
              return
            } else {
              alert('Error fetching key dates:', err)
            }
            console.error('Error fetching key dates:', err)
            return
          }

          const blob = new Blob([fetchedKeyDatesTableDataCSV], { type: 'text/csv' })

          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = 'key_dates_export.csv'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          URL.revokeObjectURL(blobUrl)

          resolve(fetchedKeyDatesTableDataCSV)
        }
      })
    })
  },
  fetchOutlookAuthUrl: async () => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchOutlookAuthUrl`,
        delay: 300,
        callback: async () => {
          const authUrl = await ospreyAPI({
            service: 'security',
            endpoint: 'Outlook/GetLoginUrl',
            method: 'GET'
          })

          resolve(authUrl)
        }
      })
    })
  },
  reset: () => {
    const keyDates = get().keyDates
    Object.keys(keyDates).forEach((key) => delete keyDates[key])
    set({ ...initialState, keyDates })
  }
}))
